/**
 * User Assignment Modal Component
 * 
 * Modal dialog for assigning users to rides that are missing user_id before upload.
 * Supports both bulk assignment (same user for all rides) and per-ride assignment modes.
 * 
 * @module UserAssignmentModal
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';

import { searchUser, getUserList, type UserSearchResult, type UserListEntry } from '../../../../services/rideUatClient';
import type { Environment } from '../EnvironmentSelector';
import { useRides } from '../../../../context/RidesContext';
import type { RideSchema } from '../../../../types/simulation';
import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
/**
 * Assignment mode type
 */
type AssignmentMode = 'bulk' | 'per-ride';

/**
 * User search/browse mode type
 */
type UserMode = 'search' | 'browse';

/**
 * Props for UserAssignmentModal component
 */
export interface UserAssignmentModalProps {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when user assignment is complete and user clicks Continue */
  onContinue: () => void;
  /** Rides that need user assignment */
  rides: RideSchema[];
  /** JWT token for API authentication */
  jwtToken: string;
  /** Product ID */
  productId: string;
  /** Network ID (UUID string) */
  networkId: string;
  /** Selected environment */
  environment: Environment;
}

/**
 * UserAssignmentModal Component
 * 
 * Modal for assigning users to rides before upload.
 */
export const UserAssignmentModal: React.FC<UserAssignmentModalProps> = ({
  open,
  onClose,
  onContinue,
  rides,
  jwtToken,
  productId,
  networkId,
  environment,
}) => {
  const { t } = useTranslation('mapTransfer');
  // Assignment mode state
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('bulk');
  
  // User search/browse mode state
  const [userMode, setUserMode] = useState<UserMode>('search');
  
  // User assignments: Map<rideId, userId>
  const [assignments, setAssignments] = useState<Map<string, string>>(new Map());
  
  // User search state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // User browse state
  const [browseResults, setBrowseResults] = useState<UserListEntry[]>([]);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const [browseOffset, setBrowseOffset] = useState(0);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browseLimit] = useState(20);
  
  // Bulk assignment: selected user for all rides
  const [bulkSelectedUser, setBulkSelectedUser] = useState<UserSearchResult | UserListEntry | null>(null);
  
  // RidesContext for updating rides
  const { updateRide } = useRides();

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setAssignmentMode('bulk');
      setUserMode('search');
      setAssignments(new Map());
      setSearchQuery('');
      setSearchResults([]);
      setBulkSelectedUser(null);
      setBrowseOffset(0);
      setBrowseResults([]);
      setSearchError(null);
      setBrowseError(null);
      
      // Attempt smart matching
      attemptSmartMatching();
      
      // Load initial browse results
      loadBrowseResults(0);
    }
  }, [open, rides]);

  /**
   * Attempt to match existing user_id patterns in rides against known users
   */
  const attemptSmartMatching = async () => {
    // Extract user_id patterns from rides that have them
    const existingUserIds = new Set<string>();
    rides.forEach(ride => {
      if (ride.user_id) {
        existingUserIds.add(ride.user_id);
      }
      if (ride.external_user_reference_id) {
        existingUserIds.add(ride.external_user_reference_id);
      }
    });

    if (existingUserIds.size === 0) {
      return; // No existing user IDs to match
    }

    // Try to find matching users by searching for the first user ID
    const firstUserId = Array.from(existingUserIds)[0];
    if (firstUserId && jwtToken && productId && networkId) {
      try {
        const results = await searchUser(
          jwtToken,
          productId,
          networkId,
          firstUserId,
          environment
        );
        
        // If we find a match, pre-assign it to rides with that user_id
        if (results.length > 0) {
          const matchedUser = results[0];
          const newAssignments = new Map<string, string>();
          
          rides.forEach(ride => {
            const rideId = ride.external_reference_id || ride.ride_id || '';
            if (ride.user_id === firstUserId || ride.external_user_reference_id === firstUserId) {
              newAssignments.set(rideId, matchedUser.id);
            }
          });
          
          if (newAssignments.size > 0) {
            setAssignments(newAssignments);
            if (assignmentMode === 'bulk' && newAssignments.size === rides.length) {
              setBulkSelectedUser(matchedUser);
            }
          }
        }
      } catch (error) {
        // Silently fail - user can still manually assign
        console.debug('[UserAssignmentModal] Smart matching failed:', error);
      }
    }
  };

  /**
   * Load browse results (paginated user list)
   */
  const loadBrowseResults = async (offset: number) => {
    if (!jwtToken || !productId || !networkId) {
      return;
    }

    setIsBrowsing(true);
    setBrowseError(null);

    try {
      const response = await getUserList(
        jwtToken,
        productId,
        networkId,
        offset,
        browseLimit,
        'createdAt',
        'DESC',
        environment
      );

      if (offset === 0) {
        setBrowseResults(response.data.entries);
      } else {
        setBrowseResults(prev => [...prev, ...response.data.entries]);
      }
      setBrowseTotal(response.data.totalEntries);
      setBrowseOffset(offset);
      
      // Attempt smart matching with browse results
      if (offset === 0) {
        performSmartMatching(response.data.entries);
      }
    } catch (error) {
      console.error('[UserAssignmentModal] Failed to load user list:', error);
      setBrowseError(error instanceof Error ? error.message : t('rides.userAssignment.loadUsersFailed'));
    } finally {
      setIsBrowsing(false);
    }
  };

  /**
   * Handle search query change with debouncing
   */
  useEffect(() => {
    if (!open || userMode !== 'search' || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, userMode, open]);

  /**
   * Perform user search
   */
  const performSearch = async (query: string) => {
    if (!jwtToken || !productId || !networkId || !query.trim()) {
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const results = await searchUser(
        jwtToken,
        productId,
        networkId,
        query,
        environment
      );
      setSearchResults(results);
      
      // Attempt smart matching with search results
      performSmartMatching(results);
    } catch (error) {
      console.error('[UserAssignmentModal] Failed to search users:', error);
      setSearchError(error instanceof Error ? error.message : t('rides.userAssignment.searchUsersFailed'));
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Handle bulk user selection
   */
  const handleBulkUserSelect = (user: UserSearchResult | UserListEntry) => {
    setBulkSelectedUser(user);
    
    // Apply to all rides
    const newAssignments = new Map<string, string>();
    rides.forEach(ride => {
      const rideId = ride.external_reference_id || ride.ride_id || '';
      newAssignments.set(rideId, user.id);
    });
    setAssignments(newAssignments);
  };

  /**
   * Handle per-ride user selection
   */
  const handlePerRideUserSelect = (rideId: string, user: UserSearchResult | UserListEntry) => {
    const newAssignments = new Map(assignments);
    newAssignments.set(rideId, user.id);
    setAssignments(newAssignments);
  };

  /**
   * Get user display name
   */
  const getUserDisplayName = (user: UserSearchResult | UserListEntry): string => {
    // Both types have firstName and lastName, but UserListEntry has more properties
    const firstName = 'firstName' in user ? user.firstName : '';
    const lastName = 'lastName' in user ? user.lastName : '';
    const name = `${firstName} ${lastName}`.trim();
    if (name) return name;
    
    // Check for email (both types may have it)
    const email = 'email' in user ? user.email : undefined;
    if (email) return email;
    
    // Both types have id
    return user.id;
  };

  /**
   * Get user email
   */
  const getUserEmail = (user: UserSearchResult | UserListEntry): string | undefined => {
    return 'email' in user ? user.email : undefined;
  };

  /**
   * Check if all rides have users assigned
   */
  const allRidesAssigned = rides.every(ride => {
    const rideId = ride.external_reference_id || ride.ride_id || '';
    return assignments.has(rideId);
  });

  /**
   * Handle Continue button click
   */
  const handleContinue = () => {
    if (!allRidesAssigned) {
      return;
    }

    // Update rides in RidesContext
    assignments.forEach((userId, rideId) => {
      const ride = rides.find(r => {
        const rId = r.external_reference_id || r.ride_id || '';
        return rId === rideId;
      });
      if (ride) {
        updateRide({
          ...ride,
          user_id: userId,
          external_user_reference_id: userId,
        });
      }
    });

    // Close modal and proceed
    onContinue();
  };

  /**
   * Handle Cancel button click
   */
  const handleCancel = () => {
    onClose();
  };

  /**
   * Perform smart matching when user results are loaded
   */
  const performSmartMatching = (users: (UserSearchResult | UserListEntry)[]) => {
    // Extract user_id patterns from rides that have them
    const existingUserIds = new Set<string>();
    rides.forEach(ride => {
      if (ride.user_id) {
        existingUserIds.add(ride.user_id);
      }
      if (ride.external_user_reference_id) {
        existingUserIds.add(ride.external_user_reference_id);
      }
    });

    if (existingUserIds.size === 0) {
      return; // No existing user IDs to match
    }

    // Try to match existing user IDs with loaded users
    const newAssignments = new Map(assignments);
    let hasNewMatches = false;

    rides.forEach(ride => {
      const rideId = ride.external_reference_id || ride.ride_id || '';
      
      // Skip if already assigned
      if (newAssignments.has(rideId)) {
        return;
      }

      // Try to find matching user
      const rideUserId = ride.user_id || ride.external_user_reference_id;
      if (rideUserId) {
        const matchedUser = users.find(u => u.id === rideUserId);
        if (matchedUser) {
          newAssignments.set(rideId, matchedUser.id);
          hasNewMatches = true;
        }
      }
    });

    if (hasNewMatches) {
      setAssignments(newAssignments);
      
      // If all rides are matched with the same user in bulk mode, select that user
      if (assignmentMode === 'bulk') {
        const assignedUserIds = Array.from(newAssignments.values());
        if (assignedUserIds.length === rides.length && new Set(assignedUserIds).size === 1) {
          const matchedUser = users.find(u => u.id === assignedUserIds[0]);
          if (matchedUser) {
            setBulkSelectedUser(matchedUser);
          }
        }
      }
    }
  };

  /**
   * Get assigned user for a ride
   */
  const getAssignedUser = (rideId: string): UserSearchResult | UserListEntry | null => {
    const userId = assignments.get(rideId);
    if (!userId) {
      return null;
    }

    // Find user in search results or browse results
    const user = [...searchResults, ...browseResults].find(u => u.id === userId);
    return user || null;
  };

  const assignedCount = Array.from(assignments.values()).filter(v => v !== null && v !== undefined).length;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 2500 }}
      PaperProps={{
        sx: {
          minHeight: '600px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon size={20} name="user" />
            <Typography variant="h6">{t('rides.userAssignment.title')}</Typography>
          </Box>
          <IconButton onClick={handleCancel} size="small">
            <Icon size={20} name="xmark" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Assignment Mode Toggle */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('rides.userAssignment.modeLabel')}
            </Typography>
            <Tooltip
              title={
                assignmentMode === 'bulk'
                  ? t('rides.userAssignment.modeBulkTip')
                  : t('rides.userAssignment.modePerRideTip')
              }
              arrow
              placement="top"
            >
              <IconButton size="small" sx={{ ml: 1 }}>
                <Icon name="triangle-exclamation" size={20} />
              </IconButton>
            </Tooltip>
          </Box>
          <Tabs
            value={assignmentMode}
            onChange={(_, newValue) => setAssignmentMode(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{t('rides.userAssignment.bulkLabel')}</span>
                  <Tooltip
                    title={t('rides.userAssignment.bulkTabTooltip')}
                    arrow
                  >
                    <Icon name="circle-info" size={20} style={{ color: '#52565e' }} />
                  </Tooltip>
                </Box>
              }
              value="bulk"
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{t('rides.userAssignment.perRideLabel')}</span>
                  <Tooltip
                    title={t('rides.userAssignment.perRideTabTooltip')}
                    arrow
                  >
                    <Icon name="circle-info" size={20} style={{ color: '#52565e' }} />
                  </Tooltip>
                </Box>
              }
              value="per-ride"
            />
          </Tabs>
        </Box>

        {/* Assignment Summary */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('rides.userAssignment.needAssignmentSummary', { count: rides.length })}
          </Typography>
          <Typography variant="body2" color={allRidesAssigned ? 'success.main' : 'warning.main'}>
            {t('rides.userAssignment.assignedSummary', { assigned: assignedCount, total: rides.length })}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* User Search/Browse Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant={userMode === 'search' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setUserMode('search')}
              startIcon={<Icon size={20} name="magnifying-glass" />}
            >
              {t('rides.userAssignment.search')}
            </Button>
            <Button
              variant={userMode === 'browse' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setUserMode('browse')}
            >
              {t('rides.userAssignment.browse')}
            </Button>
          </Box>

          {userMode === 'search' && (
            <Box>
              <TextField
                fullWidth
                label={t('rides.userAssignment.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Icon size={20} name="magnifying-glass" style={{ marginRight: 8, color: '#52565e' }} />,
                }}
                sx={{ mb: 2 }}
              />
              {isSearching && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {searchError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {searchError}
                </Alert>
              )}
              {!isSearching && searchQuery && searchResults.length === 0 && !searchError && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('rides.userAssignment.noResults', { query: searchQuery })}
                </Alert>
              )}
              {searchResults.length > 0 && (
                <List sx={{ maxHeight: '200px', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {searchResults.map((user) => (
                    <ListItem key={user.id} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          if (assignmentMode === 'bulk') {
                            handleBulkUserSelect(user);
                          }
                        }}
                        selected={assignmentMode === 'bulk' && bulkSelectedUser?.id === user.id}
                      >
                        <ListItemText
                          primary={getUserDisplayName(user)}
                          secondary={getUserEmail(user) || t('rides.userAssignment.idFallback', { value: `${user.id.substring(0, 20)}...` })}
                        />
                        {assignmentMode === 'bulk' && bulkSelectedUser?.id === user.id && (
                          <Icon size={20} name="circle-check" />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {userMode === 'browse' && (
            <Box>
              {isBrowsing && browseResults.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {browseError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {browseError}
                </Alert>
              )}
              {browseResults.length > 0 && (
                <>
                  <List sx={{ maxHeight: '200px', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                    {browseResults.map((user) => (
                      <ListItem key={user.id} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            if (assignmentMode === 'bulk') {
                              handleBulkUserSelect(user);
                            }
                          }}
                          selected={assignmentMode === 'bulk' && bulkSelectedUser?.id === user.id}
                        >
                          <ListItemText
                            primary={getUserDisplayName(user)}
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {getUserEmail(user)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {t('rides.userAssignment.displayIdStatus', { displayId: user.displayId || t('rides.userAssignment.notAvailable'), status: user.status || t('rides.userAssignment.notAvailable') })}
                                </Typography>
                              </Box>
                            }
                          />
                          {assignmentMode === 'bulk' && bulkSelectedUser?.id === user.id && (
                            <Icon size={20} name="circle-check" />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  {browseResults.length < browseTotal && (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => loadBrowseResults(browseOffset + browseLimit)}
                      disabled={isBrowsing}
                    >
                      {t('rides.userAssignment.loadMore', { remaining: browseTotal - browseResults.length })}
                    </Button>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Rides List */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('rides.userAssignment.ridesToAssign')}
          </Typography>
          <List sx={{ maxHeight: '300px', overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {rides.map((ride) => {
              const rideId = ride.external_reference_id || ride.ride_id || '';
              const assignedUser = getAssignedUser(rideId);
              const isAssigned = !!assignedUser;

              return (
                <ListItem key={rideId}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight={isAssigned ? 'normal' : 'bold'}>
                        {ride.external_reference_id || ride.ride_id || t('rides.userAssignment.unknownRide')}
                      </Typography>
                      {isAssigned ? (
                        <Chip
                          icon={<Icon size={20} name="circle-check" />}
                          label={t('rides.userAssignment.userAssignedChip', { value: getUserDisplayName(assignedUser) })}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<Icon size={20} name="triangle-exclamation" />}
                          label={t('rides.userAssignment.needsUser')}
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                    {ride.from_address && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('rides.userAssignment.fromLabel', { value: ride.from_address })}
                      </Typography>
                    )}
                    {ride.to_address && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('rides.userAssignment.toLabel', { value: ride.to_address })}
                      </Typography>
                    )}
                    {assignmentMode === 'per-ride' && (
                      <Box sx={{ mt: 1 }}>
                        <Autocomplete
                          size="small"
                          options={[...searchResults, ...browseResults]}
                          getOptionLabel={(option) => getUserDisplayName(option)}
                          value={assignedUser}
                          onChange={(_, newValue) => {
                            if (newValue) {
                              handlePerRideUserSelect(rideId, newValue);
                            } else {
                              const newAssignments = new Map(assignments);
                              newAssignments.delete(rideId);
                              setAssignments(newAssignments);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('rides.userAssignment.selectUser')}
                              placeholder={t('rides.userAssignment.selectUserPlaceholder')}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box>
                                <Typography variant="body2">{getUserDisplayName(option)}</Typography>
                                {getUserEmail(option) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {getUserEmail(option)}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {!allRidesAssigned && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('rides.userAssignment.assignWarning')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>{t('rides.userAssignment.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!allRidesAssigned}
          startIcon={<Icon size={20} name="circle-check" />}
        >
          {t('rides.userAssignment.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

